import { chatModelFor, isInternalAccount, ReasoningEffort, PlanEfforts, effortForPlan, nativeEffort, savedReasoningEffort, saveReasoningEffort } from '../../utils/chatModelCapabilities';
import React, { createContext, useCallback, useEffect, useRef, useState, useTransition } from 'react';
import { toast } from 'react-toastify';
import { useAuth0, AuthorizationParams } from "@auth0/auth0-react";
import { API_BASE } from '../Constants';
import { ChatMessage } from '../interfaces';
import Pusher from 'pusher-js';
import { CurrentChatInfo } from '../types/CurrentChatInfo';
import { useFeatureFlags } from './FeatureFlagsContext';

export interface childrenProps {
  children: React.ReactNode;
}

export interface ChatControlProviderProps {
  activeChatID: CurrentChatInfo;
  setActiveChatID: (p: CurrentChatInfo) => void;
  modelChat: string;
  setModelChat: (p: string) => void;
  reasoningEffort: ReasoningEffort;
  setReasoningEffort: (effort: ReasoningEffort) => void;
  syncReasoningEffort: (effort: ReasoningEffort, chatId: string) => void;
  planEfforts: PlanEfforts;
  userChats: object[];
  setUserChats: (p: object[]) => void;
  messageHistory: ChatMessage[];
  setMessageHistory: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  loadingHistory: boolean;
  setLoadingHistory: (p: boolean) => void;
  loadingInitialChats: boolean;
  setLoadingInitialChats: (p: boolean) => void;
  pusherObj: Pusher | undefined;
}

const ChatControlContext = createContext<ChatControlProviderProps | null>(null);

export const ChatControlProvider = ({ children }: childrenProps) => {
  const featureFlags = useFeatureFlags();
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [activeChatID, setActiveChatIDState] = useState<CurrentChatInfo>({"chatID": ""});
  const activeChatRef = useRef('');
  const effortRevision = useRef(0);
  const [selectedEffort, setSelectedEffort] = useState<ReasoningEffort | null>(savedReasoningEffort);
  const [planEfforts, setPlanEfforts] = useState<PlanEfforts>(null);
  const reasoningEffort = effortForPlan(planEfforts, selectedEffort);
  const effortWrites = useRef(new Map<string, Promise<void>>());
  const setActiveChatID = useCallback((next: CurrentChatInfo) => {
    if (next.chatID !== activeChatRef.current) {
      effortRevision.current += 1;
      // skipUpdate is used when the first send creates the conversation:
      // keep the draft's choice already persisted by /chat/create.
      if (!next.skipUpdate) setSelectedEffort(next.chatID ? null : savedReasoningEffort());
      setLoadingHistory(!!next.chatID && !next.skipUpdate);
    }
    activeChatRef.current = next.chatID;
    setActiveChatIDState(next);
  }, []);
  // A server event (`model`) belongs to the stream that emitted it: its
  // handlers stay captured in that stream, so the caller names the origin
  // chat and the sync only applies while that chat is still on screen.
  const syncReasoningEffort = useCallback((effort: ReasoningEffort, chatId: string) => {
    if (chatId !== activeChatRef.current) return;
    effortRevision.current += 1;
    setSelectedEffort(effort);
  }, []);
  // A deliberate selection applies to the chat in the ref, not to the
  // rendered activeChatID: a click in the same tick as a chat switch (before
  // this provider re-renders) belongs to the chat just opened.
  const setReasoningEffort = useCallback((effort: ReasoningEffort) => {
    const chatId = activeChatRef.current;
    const chosen = effortForPlan(planEfforts, effort);
    syncReasoningEffort(chosen, chatId);
    // Only a deliberate selection changes the default for NEW conversations.
    // Loading history or receiving plan/model updates never changes it.
    saveReasoningEffort(chosen);
    if (!chatId) return;
    const revision = effortRevision.current;
    const write = (effortWrites.current.get(chatId) ?? Promise.resolve()).then(async () => {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE}/api/chat/${chatId}/reasoning-effort`, {
        method: 'PATCH',
        headers: { Authorization: `Auth ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reasoning_effort: chosen }),
      });
      if (!response.ok) throw new Error('Could not save reasoning effort');
      const data = await response.json();
      if (activeChatRef.current === chatId && effortRevision.current === revision) {
        setSelectedEffort(nativeEffort(data.reasoning_effort));
      }
    }).catch(() => {
      toast.error('Não foi possível salvar o esforço de raciocínio. Selecione-o novamente para tentar salvar.');
    });
    effortWrites.current.set(chatId, write);
    void write.then(() => {
      if (effortWrites.current.get(chatId) === write) effortWrites.current.delete(chatId);
    });
  }, [planEfforts, syncReasoningEffort, getAccessTokenSilently]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(`${API_BASE}/api/chat/usage`, { headers: { Authorization: `Auth ${token}` } });
        if (!response.ok || cancelled) return;
        const data = await response.json();
        if (!cancelled) setPlanEfforts({ reasoning_efforts: data.reasoning_efforts, default_reasoning_effort: data.default_reasoning_effort });
      } catch { /* The server still enforces the plan when usage is unavailable. */ }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, getAccessTokenSilently]);
  const [modelChat, setModelChat] = useState<string>(() => chatModelFor(user?.email));
  const [modelDefaultApplied, setModelDefaultApplied] = useState(false);

  const [userChats, setUserChats] = useState<object[]>([]);
  const [messageHistory, setMessageHistory] = useState<ChatMessage[]>([]);
  const [loadingHistory, setLoadingHistory] = useState<boolean>(false);
  const [loadingInitialChats, setLoadingInitialChats] = useState<boolean>(false);
  const [pusherObj, setPusherObj] = useState<Pusher>();
  const [, startHistoryTransition] = useTransition();
  // The provider mounts before Auth0 finishes loading the user's email. Apply
  // the account's model once: an internal account always chats with Sabiá 5;
  // anyone else starts on the default, then history and Pensamento own it.
  useEffect(() => {
    if (modelDefaultApplied || !isAuthenticated || !user?.email) return;
    if (isInternalAccount(user.email) || !activeChatID.chatID) setModelChat(chatModelFor(user.email));
    setModelDefaultApplied(true);
  }, [isAuthenticated, user?.email, activeChatID.chatID, modelDefaultApplied]);
  

  const providerValue: ChatControlProviderProps = {
    activeChatID,
    setActiveChatID,
    modelChat,
    setModelChat,
    reasoningEffort,
    setReasoningEffort,
    syncReasoningEffort,
    planEfforts,
    userChats,
    setUserChats,
    messageHistory,
    setMessageHistory,
    loadingHistory,
    setLoadingHistory,
    loadingInitialChats,
    setLoadingInitialChats,
    pusherObj,
  };

  useEffect(() => {
    if (isAuthenticated) {
      const url = API_BASE + "/api/chat/list";
      const fetchchats = async () => {
        setLoadingInitialChats(true);
        try {
          const access_token= await getAccessTokenSilently();

          const response = await fetch(url, {
            headers: {
              Authorization: `Auth ${access_token}`,
            },
            method: 'GET',
            }
            );
          if (!response.ok) {
            return;
          }
          const data = await response.json();
          setUserChats(data["chats"]);
        } catch (error) {
          console.error('Erro ao carregar conversas:', error);
        } finally {
          setLoadingInitialChats(false);
        }
      }
      fetchchats();
    }
  }, [isAuthenticated]);

  // fetch message history for active chat
  useEffect(() => {
    if (isAuthenticated && activeChatID["chatID"] != "") {
      const url = API_BASE + "/api/chat/history/" + activeChatID["chatID"];
      const controller = new AbortController();
      const revision = effortRevision.current;
      // true quando o setLoadingHistory(false) já foi no MESMO lote (transição)
      // que troca as mensagens — ver abaixo.
      let loadingClearedWithMessages = false;
      const fetchHistory = async () => {
        if (activeChatID["skipUpdate"]){
          return;
        }
        setLoadingHistory(true);
        try {
          // Reopening immediately after a selection must see the completed
          // save, even if navigation outruns the PATCH request.
          await effortWrites.current.get(activeChatID.chatID);
          const access_token= await getAccessTokenSilently();
          if (controller.signal.aborted) return;

          const response = await fetch(url, {
            headers: {
              Authorization: `Auth ${access_token}`,
            },
            method: 'GET',
            signal: controller.signal,
            }
            );
          
          if (response.ok){
            // if there are no messages and the chatid==message[0] chatid skip
            const data = await response.json();
            if (controller.signal.aborted) return;
            if (effortRevision.current === revision) {
              setSelectedEffort(nativeEffort(data.reasoning_effort));
            }

            if (data["messages"].length > 0 || messageHistory.length==0 || (messageHistory[0] as any)["chat_id"]!= activeChatID["chatID"] ){
              // if theere are messages in the server side history, update them
              // if there are no messages locally, update them
              // if the local message chatid is different from the active chatid, update them

              // Process the filenames
              data["messages"].forEach((message: any) => {
                if (message["files"] != null){
                  message["filenames"] = message["files"].map((file: any) => file["filename"]);
                }
              });
              startHistoryTransition(() => {
                setMessageHistory(data["messages"]);
                // Set model from the chat's stored model, or keep current. An
                // internal account stays on Sabiá 5: the backend moves the
                // chat to it on the next message.
                const chatModel = data["model"];
                if (chatModel && !isInternalAccount(user?.email)) {
                  setModelChat(chatModel);
                }
                // O indicador de carregamento desliga JUNTO com a troca das
                // mensagens. Fora da transição ele era urgente e comitava
                // antes: um frame sem o esqueleto e ainda com as mensagens do
                // chat ANTERIOR — o "pisca outro chat" ao trocar de conversa.
                setLoadingHistory(false);
              });
              loadingClearedWithMessages = true;
            }
          }else{
            setMessageHistory([])
          }
        } catch (error: any) {
          // A newer chat switch aborted this fetch — don't clobber the newer
          // chat's history with this stale request's (empty) result.
          if (controller.signal.aborted || error?.name === 'AbortError') return;
          setMessageHistory([]);
        } finally {
          // Don't flip the indicator off for a superseded (aborted) fetch — a
          // newer chat's fetch may still be in flight.
          if (!controller.signal.aborted && !loadingClearedWithMessages) setLoadingHistory(false);
        }

      }
      fetchHistory();
      return () => controller.abort();
    }else if (isAuthenticated && activeChatID["chatID"] == ""){
      setMessageHistory([])
    }
  }, [activeChatID]);



  return (
    <ChatControlContext.Provider value={providerValue}>
      {children}
    </ChatControlContext.Provider>
  );
};

export default ChatControlContext;
